import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/apiMode';
import { useTranslation } from 'react-i18next';
import { FileText, Image, Download, FolderOpen } from 'lucide-react';

export default function PartnerContent() {
  const { token } = useApp();
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(null);

  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) return;
    api.get('/api/partner/content')
      .then(r => setFiles(r.data || []))
      .catch(err => setError(err.response?.data?.error || 'Errore caricamento file'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = async (file) => {
    setDownloading(file.id);
    try {
      const r = await api.get(`/api/partner/content/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Errore download file');
    } finally {
      setDownloading(null);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '—';

  const FileIcon = ({ type }) => type === 'image'
    ? <Image size={32} color="#7c3aed" />
    : <FileText size={32} color="#1d4ed8" />;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem 1rem' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ margin: 0, color: '#1e3a8a', fontSize: '1.5rem', fontWeight: 800 }}>
          {t('partner.content.title')}
        </h1>
        <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
          {t('partner.content.subtitle')}
        </p>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#64748b' }}>{t('common.loading')}</div>
      ) : files.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '3rem', textAlign: 'center', color: '#64748b' }}>
          <FolderOpen size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p>{t('partner.content.noFiles')}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
          {files.map(file => (
            <div
              key={file.id}
              style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', transition: 'box-shadow 0.15s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <FileIcon type={file.file_type} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.1rem' }}>
                    {file.file_type?.toUpperCase()} · {fmt(file.created_at)}
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleDownload(file)}
                disabled={downloading === file.id}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '0.5rem 0.75rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}
              >
                <Download size={14} />
                {downloading === file.id ? '...' : t('partner.content.download')}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
